import styled, { css } from 'styled-components';
import { BUTTON_SIZE, BUTTON_VARIANT, ICON_POSITION } from './types';
import { ITheme } from '../../provider/theme/types';

export const BaseButton = styled.button`
  font-size: 16px;
  cursor: pointer;
  display: inline-flex;
  justify-content: center;
  align-items: center;
  gap: 4px;
  border-radius: 6px;
  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
`;

export const ButtonText = styled.span`
  font-family: Work Sans;
  font-size: inherit;
  font-style: normal;
  font-weight: 600;
  line-height: 24px;
  white-space: nowrap;
`;

export const ButtonContainer = styled(BaseButton)<{
  variant: BUTTON_VARIANT;
  size: BUTTON_SIZE;
  icon?: JSX.Element;
  iconposition: ICON_POSITION;
  text?: string;
}>`
  ${({ theme, variant }) => getButtonStyles(variant, theme)};

  flex-direction: ${(props) =>
    props.iconposition === 'right' ? 'row' : 'row-reverse'};

  ${(props) =>
    props.size === 'large' &&
    css`
      padding: ${!props.text ? '8px 10px' : '8px 16px'};
      font-size: 15px;
      max-height: 40px;
      min-height: 40px;
      min-width: 40px;
      padding-left: ${props.icon && props.iconposition === 'left' && '10px'};
      padding-right: ${props.icon && props.iconposition === 'right' && '10px'};
    `}
  ${(props) =>
    props.size === 'small' &&
    css`
      padding: ${!props.text ? '6px' : '4px 12px'};
      font-size: 13px;
      max-height: 32px;
      min-height: 32px;
      min-width: 32px;
      padding-left: ${props.icon && props.iconposition === 'left' && '6px'};
      padding-right: ${props.icon && props.iconposition === 'right' && '6px'};
    `}
`;

export const Icon = styled.div`
  display: flex;
  align-items: center;
`;

export const getButtonStyles = (variant: BUTTON_VARIANT, theme: ITheme) => {
  switch (variant) {
    case 'primary':
      return css`
        background: ${theme.colors[variant][100]};
        border: 1px solid ${theme.colors[variant][100]};
        color: ${theme.colors['white']['100']};
        &:hover {
          border: 1px solid ${theme.colors.primaryHover};
        }
        &:focus {
          border: 1px solid ${theme.colors.primaryHover};
          box-shadow: 0px 0px 0px 2px ${theme.colors[variant][20]};
        }
      `;
    case 'secondary':
      return css`
        background: ${theme.colors[variant]['non_alpha']};
        border: 1px solid ${theme.colors[variant][5]};
        color: ${theme.colors[variant][100]};
        &:hover {
          border: 1px solid ${theme.colors[variant][40]};
        }
        &:focus {
          border: 1px solid ${theme.colors[variant][40]};
          box-shadow: 0px 0px 0px 2px ${theme.colors[variant][10]};
        }
      `;
    case 'destructive':
      return css`
        background: ${theme.colors['danger']['non_alpha']};
        border: 1px solid ${theme.colors['danger']['non_alpha']};
        color: ${theme.colors['danger'][100]};
        &:hover {
          border: 1px solid ${theme.colors['danger'][40]};
        }
        &:focus {
          border: 1px solid ${theme.colors['danger'][40]};
          box-shadow: 0px 0px 0px 2px ${theme.colors['danger'][20]};
        }
      `;
    case 'red':
      return css`
        background: ${theme.colors['danger'][100]};
        border: 1px solid ${theme.colors['black'][5]};
        color: ${theme.colors['white'][100]};
        &:hover {
          border: 1px solid ${theme.colors['black'][40]};
        }
        &:focus {
          border: 1px solid ${theme.colors['black'][40]};
          box-shadow: 0px 0px 0px 2px ${theme.colors['danger'][20]};
        }
      `;
    case 'white':
      return css`
        background: ${theme.colors['white'][100]};
        border: 1px solid ${theme.colors['secondary'][5]};
        color: ${theme.colors['secondary'][100]};
        &:hover {
          border: 1px solid ${theme.colors['secondary'][40]};
        }
        &:focus {
          border: 1px solid ${theme.colors['secondary'][40]};
          box-shadow: 0px 0px 0px 2px ${theme.colors['secondary'][10]};
        }
      `;

    case 'ghost':
      return css`
        background: ${theme.colors['neutral'][5]};
        border: 1px solid ${theme.colors['black'][5]};
        color: ${theme.colors['neutral'][100]};
        &:hover {
          border: 1px solid ${theme.colors['black'][20]};
        }
        &:focus {
          border: 1px solid ${theme.colors['black'][20]};
          box-shadow: 0px 0px 0px 2px ${theme.colors['secondary'][10]};
        }
      `;

    case 'transparent':
      return css`
        background: transparent;
        border: 1px solid transparent;
      `;
    case 'gradient':
      return css`
        background: linear-gradient(
          31deg,
          #8d31d6 0%,
          ${theme.colors.primary[100]} 100%
        );
        border: 1px solid ${theme.colors.primary[100]};
        color: ${theme.colors['white']['100']};
        &:hover {
          border: 1px solid ${theme.colors.primaryHover};
        }
        &:focus {
          border: 1px solid ${theme.colors.primaryHover};
          box-shadow: 0px 0px 0px 2px ${theme.colors.primary[20]};
        }
      `;

    default:
      return css``;
  }
};
